/**
 * Rasgueo Base — rhythm-04-strum
 * Dirección (↓↑) + gesto (mano / palma / muteo). Evaluación por timing.
 */
import type { RhythmSubMission, RhythmTargetNote } from "./rhythmPathPlan";
import { makeStep } from "./rhythmPatternBuilders";

const step = (
  partial: Parameters<typeof makeStep>[1]
): RhythmSubMission => makeStep("rhythm-04-strum", partial);

const STRUM = "E2";

const down = (
  duration: number,
  gesture: "hand" | "palm" = "hand"
): RhythmTargetNote => ({
  type: "attack",
  pitch: STRUM,
  duration,
  finger: "p",
  direction: "down",
  gesture,
});

const up = (
  duration: number,
  gesture: "hand" | "palm" = "hand"
): RhythmTargetNote => ({
  type: "attack",
  pitch: STRUM,
  duration,
  finger: "p",
  direction: "up",
  gesture,
});

const mute = (duration: number): RhythmTargetNote => ({
  type: "rest",
  duration,
  gesture: "mute",
});

export const RHYTHM_04_STRUM: RhythmSubMission[] = [
  step({
    id: "strum-4-1-peso-muerto",
    title: "Peso muerto",
    subtitle: "Solo ↓ · redondas → blancas",
    blockLabel: "Bloque 1 · Peso y muteo",
    kind: "corto",
    goal: "Peso hacia abajo; luego densificar.",
    targetPattern: [
      down(4),
      down(4),
      down(4),
      down(4),
      down(2),
      down(2),
      down(2),
      down(2),
    ],
    instructions: [
      "Mira el rhythm tracker: solo flechas hacia abajo (↓).",
      "Cuatro redondas y luego cuatro blancas. Un gesto de muñeca por flecha.",
      "Rozá las 6 cuerdas hacia el suelo. El indicador dice «Mano».",
    ],
    tip: "Péndulo de muñeca. El brazo casi no empuja.",
    feedbackOk: "¡Peso ↓ estable!",
    feedbackError: "Solo rasgueos hacia abajo (↓). Sigue el tracker.",
  }),
  step({
    id: "strum-4-2-freno",
    title: "El freno (Palma)",
    subtitle: "↓ + muteo · dos densidades",
    blockLabel: "Bloque 1 · Peso y muteo",
    kind: "corto",
    goal: "Muteo con palma a dos velocidades.",
    targetPattern: [
      down(2, "palm"),
      mute(2),
      down(2, "palm"),
      mute(2),
      down(2, "palm"),
      mute(2),
      down(2, "palm"),
      mute(2),
      down(1, "palm"),
      mute(1),
      down(1, "palm"),
      mute(1),
      down(1, "palm"),
      mute(1),
      down(1, "palm"),
      mute(1),
    ],
    instructions: [
      "En el tracker: ↓ = rasgueo (palma), ✕ = muteo (apaga las cuerdas).",
      "Primero pares en blanca; luego pares en negra.",
      "Si atacas en el ✕, el paso se reinicia.",
    ],
    tip: "El muteo es parte del ritmo. Sin freno, el rasgueo se ensucia.",
    feedbackOk: "¡↓ y muteo a tiempo!",
    feedbackError: "↓ con palma y ✕ muteo. No ataques en el silencio.",
  }),
  step({
    id: "strum-4-3-negra-freno",
    title: "Negra y freno",
    subtitle: "↓↓↓ ✕ · frases",
    blockLabel: "Bloque 1 · Peso y muteo",
    kind: "medio",
    goal: "Frasear el rasgueo con el tracker.",
    targetPattern: [
      down(1),
      down(1),
      down(1),
      mute(1),
      down(1),
      down(1),
      down(1),
      mute(1),
      down(1),
      down(1),
      down(1),
      mute(1),
      down(1),
      down(1),
      down(1),
      mute(1),
    ],
    instructions: [
      "Lee el tracker: tres ↓ y un ✕. Cuatro frases.",
      "Cuenta en voz baja: uno-dos-tres-mute.",
      "El indicador pasa de Mano a Muteo en el ✕.",
    ],
    tip: "Frasear evita el trance de abajo-freno infinito.",
    feedbackOk: "¡Frases ↓↓↓ ✕!",
    feedbackError: "Tres ↓ y muteo ×4. Sigue las flechas del tracker.",
  }),
  step({
    id: "strum-4-4-resistencia-i",
    title: "Resistencia I",
    subtitle: "↓ · mute · ↓↑ · cierre",
    blockLabel: "Bloque 1 · Peso y muteo",
    kind: "resistencia",
    isEndurance: true,
    goal: "Péndulo con capítulos; introduce ↑ en corcheas.",
    targetPattern: [
      ...Array.from({ length: 8 }, () => down(1)),
      down(2, "palm"),
      mute(2),
      // Introducción ↑↓ en corcheas (mismo piso, péndulo)
      down(0.5),
      up(0.5),
      down(0.5),
      up(0.5),
      down(0.5),
      up(0.5),
      down(0.5),
      up(0.5),
      down(2, "palm"),
      mute(2),
      ...Array.from({ length: 8 }, () => down(1)),
    ],
    instructions: [
      "Capítulo 1: ocho ↓ a negra. Luego blanca ↓ (palma) + muteo.",
      "Capítulo 2: corcheas ↓↑↓↑ — mira el tracker; la flecha cambia.",
      "Otra blanca + muteo, y cierra con ocho ↓.",
    ],
    tip: "↑ es el mismo péndulo de vuelta. No empujes: deja que la muñeca suba.",
    feedbackOk: "Péndulo ↓↑ con forma. Rasgueo base listo.",
    feedbackError: "Sigue ↓, muteo y el bloque ↓↑ del tracker.",
  }),
];
